import { Component, EventEmitter, HostListener, Input, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ActivityTypologyStyle, appRoutes } from '@tekman/onmat/constants';
import { ActivitiesStep, CategoryModel, ProgressActivityModel } from '@tekman/onmat/models';
import { UserService } from '@tekman/onmat/service/generic';
import { Category, CategoryMapperService, FeaturitWrapperService } from '@tekman/onmat/service/onmat';
import { FeaturitEventActivity } from '@tekman/shared/models';
import { CloneUtility } from '@tekman/shared/utils';

@Component({
  selector: 'dropdown-selector',
  templateUrl: './dropdown-selector.component.html',
  styleUrls: ['./dropdown-selector.component.scss'],
})
export class DropdownSelectorComponent implements OnInit {
  @Input() category: string;
  @Input() typology: string;
  @Input() typologyTitle: string;
  @Input() set progress(value: ProgressActivityModel[]) {
    this._data.forEach(data => {
      data.activities.forEach(activity => {
        const findProgress = value.find(progress => progress.id === activity.id);
        if (findProgress) {
          activity.completedExcercises = findProgress.totalSavedExercises;
          activity.totalExcercises = findProgress.totalEnabledExercises;
          activity.isSent = findProgress.activitySent;
        }
      });
    });
  }

  @Input() set steps(value: CategoryModel[]) {
    this._data = CloneUtility.deepClone(value);
    this.initializeData();
  }

  @Input() stepId: string;
  @Input() stepTitle: string;
  @Input() activityId: string;
  @Input() activity: any;
  @Input() isTry: boolean;

  @Output() clickedOption: EventEmitter<ActivitiesStep> = new EventEmitter<ActivitiesStep>();

  activityTypology: typeof ActivityTypologyStyle = ActivityTypologyStyle;
  timeout: ReturnType<typeof setTimeout>;
  _data: CategoryModel[];
  open = false;
  currentActivity: string;
  isTeacher = this.userService.isTeacher();
  idElementByCategory: string;
  elHeight: number;
  public appRoutes = appRoutes;
  public genericActivity: string;

  constructor(
    private categoryMapper: CategoryMapperService,
    private readonly userService: UserService,
    private translate: TranslateService,
    private readonly featuritService: FeaturitWrapperService
  ) {}

  ngOnInit(): void {
    this.genericActivity = this.translate.instant('f.activity.typology.generic');
  }

  openCloseSelector(event) {
    this.open = !this.open;
    event.stopPropagation();
  }

  initializeData() {
    if (this._data) {
      this._data = this._data.filter(item => {
        return item.categoryTitle !== null;
      });
      this._data.forEach(data => {
        data.activities = data.activities.map(activity => {
          return {
            ...activity,
            disabled:
              (activity.style === ActivityTypologyStyle.AUTOEVALUATION ||
                activity.style === ActivityTypologyStyle.TEST_YOURSELF ||
                activity.solutionaryLink) &&
              this.isTry,
          };
        });
      });
      this.sortData();
      this.setCurrentActivityTitle();
      this.getTallestElementHeight();
    }
  }

  sortData() {
    const categoryOrder = [Category.RECURSOS, Category.ACTIVIDADES, Category.EJERCICIOS, Category.AUTOEVALUACION, Category.PRUEBAS];
    const sortedData: CategoryModel[] = [];

    categoryOrder.forEach(element => {
      const result = this._data.find(step => this.getMappedStyle(step.activities[0]?.style) === element);
      if (result) {
        sortedData.push(result);
      }
    });

    this._data = sortedData;
  }

  setCurrentActivityTitle() {
    const filteredResult = this._data
      .find(item => {
        return item.activities.find(info => info.style === this.typology);
      })
      ?.activities.filter(activity => activity.id === this.activityId);
    const titleToShow = this._data.length === 1 ? filteredResult[0]?.title : filteredResult[0]?.typologyTitle;
    this.currentActivity = filteredResult && filteredResult.length > 0 ? titleToShow : this.typologyTitle;
  }

  getTallestElementHeight() {
    const indexOfLongestActivitiesArr = this._data.reduce(
      (pending, current, idx, arr) => (arr[pending].activities.length > current.activities.length ? pending : idx),
      0
    );
    this.idElementByCategory = this._data[indexOfLongestActivitiesArr].categoryTitle;
    setTimeout(() => {
      const tallestCategoryElement = document.getElementById(this.idElementByCategory);
      this.elHeight = tallestCategoryElement?.clientHeight;
    }, 10);
  }

  getMappedStyle(style: string) {
    return this.categoryMapper.getCategory(style);
  }

  getMappedIcon(style: string) {
    return this.categoryMapper.getIcon(style);
  }

  @HostListener('document:click', ['$event']) onDocumentClick(event: any) {
    this.open = false;
    event.stopPropagation();
  }

  emitClick(step: ActivitiesStep, solutionary: boolean) {
    if (step.disabled) {
      return;
    }
    if (solutionary) {
      window.open(step.solutionaryLink);
      return;
    }
    if (step.link) {
      this.track(step, solutionary);
      window.open(step.link);
      return;
    }
    this.clickedOption.emit(step);
  }

  track(step, solutionary?: boolean) {
    const params = {
      ui_path: 'activities',
      activity_id: this.activity.id,
      step_id: step.id,
      step_title: step.title,
      activity_typology: this.categoryMapper.getCategory(step.style),
      activity_title: this.activity.title,
      activity_subtypology: step.style,
    };
    if (solutionary) {
      params['activity_feature'] = 'solutionary';
    }
    const trackingProps: FeaturitEventActivity<string> = new FeaturitEventActivity(params);
    this.featuritService.track(this.featuritService.eventKeys.activityDownload, trackingProps);
  }
}
